import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getMe } from './api'

/**
 * Curadoria de Busca (veredito busca-sábia do Cérebro, Onda 1 — 2026-07-11).
 *
 * Fila de buscas dos ATENDENTES que zeraram/fraquejaram (telemetria do robô)
 * + dicionário de sinônimos de balcão. Dois gestos do curador:
 *   🔎 Achar produto → busca da própria ferramenta (curar etiquetas = ADR-0023)
 *   ➕ Criar sinônimo → entra na cascata determinística em ~60s
 * A fila se limpa sozinha: curou → as buscas seguintes acham → sai da lista.
 *
 * API via portal (caminho absoluto — a ferramenta roda no domínio do portal;
 * standalone :3013 mostra aviso).
 */

const API = '/api/erp/busca-curadoria'

type FilaItem = { query_norm: string; vezes: number; ultima: string; melhor_resultado: number; origens: string[] }
type Sinonimo = { termo: string; expansao: string[]; ativo: boolean; criado_por: string | null; atualizado_em: string }

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(`${API}${path}`, {
    credentials: 'include',
    headers: init?.body ? { 'Content-Type': 'application/json' } : undefined,
    ...init,
  })
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? `HTTP ${r.status}`)
  return r.json()
}

function dataCurta(iso: string): string {
  const d = new Date(iso)
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

function FormSinonimo({ termoInicial, onFechar }: { termoInicial: string; onFechar: () => void }) {
  const qc = useQueryClient()
  const [termo, setTermo] = useState(termoInicial)
  const [expansao, setExpansao] = useState('')
  const salvar = useMutation({
    mutationFn: () =>
      apiFetch('/sinonimos', {
        method: 'POST',
        body: JSON.stringify({ termo, expansao: expansao.split(',').map((s) => s.trim()).filter(Boolean) }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['curadoria-sinonimos'] })
      onFechar()
    },
  })
  return (
    <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-2">
      <div className="flex gap-2 items-center">
        <label className="text-xs text-slate-600 w-28">Quando buscarem</label>
        <input value={termo} onChange={(e) => setTermo(e.target.value)}
          className="flex-1 border rounded px-2 py-1 text-sm" placeholder="ex: veneno rato" />
      </div>
      <div className="flex gap-2 items-center">
        <label className="text-xs text-slate-600 w-28">Buscar também por</label>
        <input value={expansao} onChange={(e) => setExpansao(e.target.value)}
          className="flex-1 border rounded px-2 py-1 text-sm" placeholder="raticida, ratoeira (separar por vírgula)" />
      </div>
      <div className="flex gap-2 justify-end">
        <button onClick={onFechar} className="text-sm text-slate-500 px-3 py-1">cancelar</button>
        <button onClick={() => salvar.mutate()} disabled={salvar.isPending}
          className="text-sm bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 disabled:opacity-50">
          {salvar.isPending ? 'Salvando…' : 'Salvar sinônimo'}
        </button>
      </div>
      {salvar.error && <div className="text-xs text-red-600">{String((salvar.error as Error).message)}</div>}
    </div>
  )
}

function LinhaFila({ item, isAdmin }: { item: FilaItem; isAdmin: boolean }) {
  const [criando, setCriando] = useState(false)
  return (
    <div className="py-2 border-b border-slate-100 last:border-0">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="font-medium text-slate-800">“{item.query_norm}”</span>
        <span className="text-xs text-slate-500">{item.vezes}× · última {dataCurta(item.ultima)}</span>
        {item.melhor_resultado > 0 && (
          <span className="text-xs text-amber-600">só {item.melhor_resultado} resultado(s)</span>
        )}
        <span className="flex-1" />
        <Link to={`/?q=${encodeURIComponent(item.query_norm)}`}
          className="text-xs bg-slate-100 hover:bg-slate-200 px-2 py-1 rounded">
          🔎 Achar produto
        </Link>
        {isAdmin && (
          <button onClick={() => setCriando((v) => !v)}
            className="text-xs bg-slate-100 hover:bg-slate-200 px-2 py-1 rounded">
            ➕ Criar sinônimo
          </button>
        )}
      </div>
      {criando && <FormSinonimo termoInicial={item.query_norm} onFechar={() => setCriando(false)} />}
    </div>
  )
}

export default function CuradoriaBusca() {
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: getMe, retry: false })
  const isAdmin = !!me?.is_admin

  const fila = useQuery({
    queryKey: ['curadoria-fila'],
    queryFn: () => apiFetch<{ zeradas: FilaItem[]; fracas: FilaItem[] }>('/fila'),
  })
  const sinonimos = useQuery({
    queryKey: ['curadoria-sinonimos'],
    queryFn: () => apiFetch<{ sinonimos: Sinonimo[] }>('/sinonimos'),
  })
  const qc = useQueryClient()
  const toggle = useMutation({
    mutationFn: (s: Sinonimo) =>
      apiFetch(`/sinonimos/${encodeURIComponent(s.termo)}`, { method: 'PUT', body: JSON.stringify({ ativo: !s.ativo }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['curadoria-sinonimos'] }),
  })
  const excluir = useMutation({
    mutationFn: (termo: string) => apiFetch(`/sinonimos/${encodeURIComponent(termo)}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['curadoria-sinonimos'] }),
  })
  const [novoAberto, setNovoAberto] = useState(false)

  if (fila.error) {
    return (
      <div className="max-w-[900px] mx-auto p-6 text-sm text-slate-600">
        <p className="text-red-600 font-medium">Não consegui carregar a fila de curadoria.</p>
        <p className="mt-1">Esta aba funciona pela casca do portal (/erp/catalogo). Detalhe: {String((fila.error as Error).message)}</p>
      </div>
    )
  }

  return (
    <div className="max-w-[900px] mx-auto p-4 space-y-6">
      <div>
        <h2 className="text-lg font-bold text-slate-800">🔍 Curadoria de Busca</h2>
        <p className="text-xs text-slate-500 mt-0.5">
          O que os atendentes buscaram no WhatsApp e a loja não soube responder (14 dias).
          Cure as etiquetas do produto (🔎) ou ensine um sinônimo (➕) — a busca aprende na hora,
          e o item some da fila sozinho quando as próximas buscas passarem a achar.
        </p>
      </div>

      <section className="bg-white rounded-xl border border-slate-200 p-4">
        <h3 className="text-sm font-semibold text-red-700 mb-1">Buscas que ZERARAM</h3>
        {fila.isLoading && <p className="text-sm text-slate-400">carregando…</p>}
        {fila.data && fila.data.zeradas.length === 0 && (
          <p className="text-sm text-emerald-600">Nenhuma busca zerada nos últimos 14 dias 🎉</p>
        )}
        {fila.data?.zeradas.map((i) => <LinhaFila key={i.query_norm} item={i} isAdmin={isAdmin} />)}
      </section>

      <section className="bg-white rounded-xl border border-slate-200 p-4">
        <h3 className="text-sm font-semibold text-amber-700 mb-1">Buscas FRACAS (1-2 resultados)</h3>
        {fila.data && fila.data.fracas.length === 0 && (
          <p className="text-sm text-slate-400">Nada por aqui.</p>
        )}
        {fila.data?.fracas.map((i) => <LinhaFila key={i.query_norm} item={i} isAdmin={isAdmin} />)}
      </section>

      <section className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-slate-700">
            Sinônimos de balcão {sinonimos.data ? `(${sinonimos.data.sinonimos.length})` : ''}
          </h3>
          {isAdmin && (
            <button onClick={() => setNovoAberto((v) => !v)}
              className="text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700">
              ➕ novo sinônimo
            </button>
          )}
        </div>
        {novoAberto && <FormSinonimo termoInicial="" onFechar={() => setNovoAberto(false)} />}
        <div className="mt-2 divide-y divide-slate-100">
          {sinonimos.data?.sinonimos.map((s) => (
            <div key={s.termo} className="py-1.5 flex items-center gap-2 text-sm">
              <span className={`font-medium ${s.ativo ? 'text-slate-800' : 'text-slate-400 line-through'}`}>
                {s.termo}
              </span>
              <span className="text-slate-400">→</span>
              <span className="text-slate-600 flex-1 truncate">{s.expansao.join(', ')}</span>
              {isAdmin && (
                <>
                  <button onClick={() => toggle.mutate(s)}
                    className="text-xs text-slate-500 hover:text-slate-800" title={s.ativo ? 'pausar' : 'reativar'}>
                    {s.ativo ? '⏸' : '▶️'}
                  </button>
                  <button onClick={() => { if (confirm(`Excluir "${s.termo}"?`)) excluir.mutate(s.termo) }}
                    className="text-xs text-red-400 hover:text-red-600" title="excluir">
                    🗑
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  )
}
